/**
 * Copyright 2025 Amazon.com, Inc. and its affiliates. All Rights Reserved.
 *
 * Licensed under the Amazon Software License (the "License").
 * You may not use this file except in compliance with the License.
 * A copy of the License is located at
 *
 *   http://aws.amazon.com/asl/
 *
 * or in the "license" file accompanying this file. This file is distributed
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the License for the specific language governing
 * permissions and limitations under the License.
 */

import React from 'react';
import { Alert, Box } from '@cloudscape-design/components';
import { isDevelopment, shouldBypassAuth, getBackendEndpoint } from '../../lib/config';

/**
 * DevelopmentModeBanner displays a warning banner when in development mode
 * Shows configuration information and authentication status
 * Requirements: 17.2
 */
export const DevelopmentModeBanner: React.FC = () => {
  const isDevMode = isDevelopment();
  const authBypassed = shouldBypassAuth();

  if (!isDevMode) {
    return null;
  }

  const backendUrl = getBackendEndpoint();

  return (
    <Alert
      type="warning"
      dismissible={false}
      header="Development Mode Active"
    >
      <Box>
        <p>
          You are running in development mode. This banner will not appear in production.
        </p>
        <ul style={{ marginTop: '8px', marginBottom: 0 }}>
          <li>
            <strong>Authentication:</strong> {authBypassed ? 'Bypassed' : 'Enabled'}
          </li>
          <li>
            <strong>Backend Endpoint:</strong> <code>{backendUrl}</code>
          </li>
          <li>
            <strong>Environment:</strong> Development
          </li>
        </ul>
      </Box>
    </Alert>
  );
};
